import { QUESTIONS, XP_BY_DIFFICULTY } from '../data/questions.js';

/**
 * Question presentation.
 *
 * In questions.js the correct answer is always `options[0]` so the data stays
 * readable and reviewable. That would make the quiz trivially gameable, so the
 * options are shuffled here before display — deterministically, seeded from the
 * question id, so a given question always presents the same way and a player
 * can't reroll a hard question by refreshing.
 */

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Small, fast, seedable PRNG (mulberry32). */
function seededRandom(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fisher–Yates over an index array, so we can remap `answer` afterwards. */
function shuffledIndices(length, rand) {
  const idx = Array.from({ length }, (_, i) => i);
  for (let i = idx.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [idx[i], idx[j]] = [idx[j], idx[i]];
  }
  return idx;
}

/** @returns the question with `options` shuffled and `answer` remapped. */
export function presentQuestion(question) {
  const rand = seededRandom(hashString(question.id));
  const order = shuffledIndices(question.options.length, rand);
  return {
    ...question,
    options: order.map((i) => question.options[i]),
    answer: order.indexOf(question.answer),
  };
}

/**
 * Build the deck for a quiz run.
 *
 * Ordering is seeded from the day key so everyone gets a stable "today's quiz"
 * that reshuffles tomorrow, rather than a different order on every mount.
 */
export function buildQuizSet({ seed = 'default', limit = null, categories = null } = {}) {
  let pool = QUESTIONS;
  if (categories?.length) {
    pool = pool.filter((q) => categories.includes(q.category));
  }

  const rand = seededRandom(hashString(seed));
  const order = shuffledIndices(pool.length, rand);
  const shuffled = order.map((i) => pool[i]);

  const sliced = limit == null ? shuffled : shuffled.slice(0, limit);
  return sliced.map(presentQuestion);
}

export function xpForQuestion(question) {
  return XP_BY_DIFFICULTY[question.difficulty] ?? 10;
}
